<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiIcon from './PosUiIcon.vue';
  import { ariaBoolean } from './pos-ui-aria.utils';

  type TSize = 'sm' | 'md' | 'lg';
  type TAriaAutocomplete = 'none' | 'inline' | 'list' | 'both';

  const props = withDefaults(
    defineProps<{
      value?: string | number | null;
      icon?: string;
      placeholder?: string;
      size?: TSize;
      maxlength?: number;
      type?: string;
      id?: string;
      min?: string | number;
      max?: string | number;
      step?: string | number;
      readonly?: boolean;
      disabled?: boolean;
      clearable?: boolean;
      inputmode?: string;
      /** Static unit shown before the value (e.g. a currency symbol). */
      prefix?: string;
      /** Static unit shown after the value (e.g. "puntos", "%"). */
      suffix?: string;
      /** Browser autofill hint forwarded to the native input (e.g. "email", "tel"). */
      autocomplete?: string;
      // ARIA of the native input (a labelled field, a combobox with its listbox).
      ariaLabel?: string;
      ariaDescribedby?: string;
      role?: string;
      ariaActivedescendant?: string;
      ariaControls?: string;
      ariaExpanded?: boolean;
      ariaAutocomplete?: TAriaAutocomplete;
    }>(),
    {
      value: '',
      icon: undefined,
      placeholder: undefined,
      size: 'md',
      maxlength: undefined,
      type: 'text',
      id: undefined,
      min: undefined,
      max: undefined,
      step: undefined,
      readonly: false,
      disabled: false,
      clearable: false,
      inputmode: undefined,
      prefix: undefined,
      suffix: undefined,
      autocomplete: undefined,
      ariaLabel: undefined,
      ariaDescribedby: undefined,
      role: undefined,
      ariaActivedescendant: undefined,
      ariaControls: undefined,
      // undefined (not Vue's Boolean false): no `aria-expanded` unless given.
      ariaExpanded: undefined,
      ariaAutocomplete: undefined,
    }
  );

  const emit = defineEmits<{
    (e: 'input', value: string): void;
    (e: 'enter'): void;
    (e: 'focus', event: FocusEvent): void;
    (e: 'blur', event: FocusEvent): void;
    (e: 'keydown', event: KeyboardEvent): void;
  }>();

  const showClear = computed(
    () => props.clearable && !props.disabled && String(props.value ?? '') !== ''
  );

  function onInput(ev: Event) {
    emit('input', (ev.target as HTMLInputElement).value);
  }
</script>

<template>
  <div
    class="pos-ui-input"
    :class="[
      `size-${size}`,
      {
        'has-icon': !!icon,
        'has-prefix': !!prefix,
        'has-suffix': !!suffix,
        'is-disabled': disabled,
      },
    ]"
  >
    <PosUiIcon v-if="icon" :name="icon" class="pos-ui-input-icon" />
    <span v-if="prefix" class="pos-ui-input-prefix">{{ prefix }}</span>
    <input
      class="pos-ui-input-field"
      :value="value ?? ''"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :type="type"
      :id="id"
      :min="min"
      :max="max"
      :step="step"
      :readonly="readonly"
      :disabled="disabled"
      :inputmode="inputmode"
      :autocomplete="autocomplete"
      :aria-label="ariaLabel"
      :aria-describedby="ariaDescribedby"
      :role="role"
      :aria-activedescendant="ariaActivedescendant"
      :aria-controls="ariaControls"
      :aria-expanded="ariaBoolean(ariaExpanded)"
      :aria-autocomplete="ariaAutocomplete"
      @input="onInput"
      @keyup.enter="emit('enter')"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
      @keydown="emit('keydown', $event)"
    />
    <span v-if="suffix" class="pos-ui-input-suffix">{{ suffix }}</span>
    <button
      v-if="showClear"
      class="pos-ui-input-clear"
      type="button"
      :aria-label="$t('posV2.clear')"
      @click="emit('input', '')"
    >
      <PosUiIcon name="close" />
    </button>
  </div>
</template>

<style scoped lang="scss">
  .pos-ui-input {
    @apply tw-relative tw-flex tw-w-full tw-items-center tw-rounded-lg tw-border tw-border-neutral-300 tw-bg-surface tw-transition-colors;

    &:focus-within {
      @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
    }

    &.is-disabled {
      @apply tw-bg-neutral-50 tw-opacity-60;
    }

    &.size-sm {
      @apply tw-h-9;
      .pos-ui-input-field,
      .pos-ui-input-prefix,
      .pos-ui-input-suffix {
        @apply tw-text-sm;
      }
    }
    &.size-md {
      @apply tw-h-11;
      .pos-ui-input-field,
      .pos-ui-input-prefix,
      .pos-ui-input-suffix {
        @apply tw-text-sm;
      }
    }
    &.size-lg {
      @apply tw-h-12;
      .pos-ui-input-field,
      .pos-ui-input-prefix,
      .pos-ui-input-suffix {
        @apply tw-text-base;
      }
    }

    .pos-ui-input-icon {
      @apply tw-pointer-events-none tw-pl-3 tw-text-xl tw-text-neutral-400;
    }

    .pos-ui-input-prefix {
      @apply tw-pointer-events-none tw-flex-none tw-select-none tw-whitespace-nowrap tw-pl-3 tw-text-neutral-400;
    }

    .pos-ui-input-suffix {
      @apply tw-pointer-events-none tw-flex-none tw-select-none tw-whitespace-nowrap tw-pr-3 tw-text-neutral-400;
    }

    .pos-ui-input-field {
      @apply tw-h-full tw-w-full tw-min-w-0 tw-flex-1 tw-rounded-lg tw-bg-transparent tw-px-3 tw-text-neutral-700 tw-outline-none placeholder:tw-text-neutral-400;
    }

    &.has-icon .pos-ui-input-field,
    &.has-prefix .pos-ui-input-field {
      @apply tw-pl-2;
    }

    &.has-icon .pos-ui-input-prefix {
      @apply tw-pl-2;
    }

    &.has-suffix .pos-ui-input-field {
      @apply tw-pr-2;
    }

    .pos-ui-input-clear {
      @apply tw-flex tw-h-full tw-items-center tw-pr-3 tw-text-neutral-400 hover:tw-text-neutral-600;
    }
  }
</style>
